/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */

/**
 * CustomPointHelper.js
 *
 * Helper for rendering a set of points as sprites (billboarded icons or circles) in Three.js.
 * Supports multiple point styles (circle, ring, cloud) and efficient texture usage.
 *
 * Usage: new CustomPointHelper(points, options)
 *   - points: Array of { position: [x, y, z], color: '#rrggbb' }
 *   - options: { size, texture, opacity, pointStyle, ... }
 *
 * Author: [Your Name]
 * Date: [Update as needed]
 */


import * as THREE from 'three';
import { Group } from 'three';

type PointStyleType = 'circle' | 'ring' | 'cloud';
type PointTextureOptions = {
    size?: number;
    pointStyle?: PointStyleType;
    pointColor?: string;
    ringWidth?: number;
    innerAlpha?: number;
    midAlpha?: number;
    outerAlpha?: number;
};
type Point = { position: [number, number, number]; color?: string };
type PointHelperOptions = {
    size?: number;
    texture?: THREE.Texture;
    opacity?: number;
    pointStyle?: PointStyleType;
    ringWidth?: number;
    ringColor?: string;
    centerOpacity?: number;
    edgeOpacity?: number;
};

// Enum for point styles
export const PointStyle = Object.freeze({
    CIRCLE: 'circle',
    RING: 'ring',
    CLOUD: 'cloud',
});



// Greyscale radial alpha texture for cloud style (cached)

// Greyscale radial alpha texture for cloud style (cached)
let cachedCloudTexture: THREE.Texture | null = null;

/**
 * Creates a canvas-based texture for a point sprite, supporting circle, ring, and cloud styles.
 *
 * @param {Object} options
 * @param {number} [options.size=256] - Texture size in pixels
 * @param {string} [options.pointStyle=PointStyle.CIRCLE] - Point style
 * @param {string} [options.pointColor='#fff'] - Color for circle/ring
 * @param {number} [options.ringWidth=0.18] - Ring width (fraction of radius)
 * @param {number} [options.innerAlpha=0.8] - Inner alpha for cloud
 * @param {number} [options.midAlpha=0.3] - Mid alpha for cloud
 * @param {number} [options.outerAlpha=0.0] - Outer alpha for cloud
 * @returns {THREE.Texture}
 */
function createPointTexture({
    size = 256,
    pointStyle = PointStyle.CIRCLE,
    pointColor = '#fff',
    ringWidth = 0.18,
    innerAlpha = 0.8,
    midAlpha = 0.3,
    outerAlpha = 0.0,
}: PointTextureOptions = {}): THREE.Texture {
    if (!Object.values(PointStyle).includes(pointStyle)) {
        throw new Error(`Invalid pointStyle: ${pointStyle}`);
    }
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('2D canvas context is unavailable for point texture creation');
    }
    ctx.clearRect(0, 0, size, size);
    const center = size / 2;
    let radius = center - (ringWidth * center);
    if (pointStyle === PointStyle.RING) {
        // Draw ring
        ctx.save();
        ctx.beginPath();
        ctx.arc(center, center, radius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.lineWidth = Math.max(2, radius * ringWidth);
        ctx.strokeStyle = pointColor;
        ctx.shadowColor = pointColor;
        ctx.shadowBlur = size * 0.08;
        ctx.stroke();
        ctx.restore();
    } else if (pointStyle === PointStyle.CIRCLE) {
        // Draw filled circle
        ctx.save();
        ctx.beginPath();
        ctx.arc(center, center, radius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.fillStyle = pointColor;
        ctx.shadowColor = pointColor;
        ctx.shadowBlur = size * 0.08;
        ctx.fill();
        ctx.restore();
    } else if (pointStyle === PointStyle.CLOUD) {
        // Use cached greyscale alpha texture if available
        if (cachedCloudTexture) return cachedCloudTexture;
        ctx.save();
        // For cloud, use the full center as radius so the gradient fills the sprite
        radius = center;
        const grad = ctx.createRadialGradient(center, center, 0, center, center, radius);
        grad.addColorStop(0.0, `rgba(255,255,255,${innerAlpha})`);
        grad.addColorStop(0.4, `rgba(255,255,255,${midAlpha})`);
        grad.addColorStop(1.0, `rgba(255,255,255,${outerAlpha})`);
        ctx.beginPath();
        ctx.arc(center, center, radius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.restore();
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.needsUpdate = true;
        cachedCloudTexture = texture;
        return texture;
    }
    // For circle/ring, always return a new texture (color may differ)
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.needsUpdate = true;
    return texture;
}


/**
 * CustomPointHelper
 *
 * Three.js Group subclass for rendering a set of points as sprites (billboarded icons or circles).
 * Supports circle, ring, and cloud styles. Efficiently shares textures for each style.
 *
 * @class
 * @extends THREE.Group
 * @param {Array} points - Array of { position: [x, y, z], color: '#rrggbb' }
 * @param {Object} options - Options for rendering
 * @param {number} [options.size=0.04] - Sprite size
 * @param {THREE.Texture} [options.texture] - Custom texture (optional)
 * @param {number} [options.opacity=1.0] - Sprite opacity
 * @param {string} [options.pointStyle=PointStyle.CIRCLE] - Point style
 * @param {number} [options.ringWidth=0.18] - Ring width (for ring style)
 * @param {string} [options.ringColor='#fff'] - Ring color (for ring style)
 * @param {number} [options.centerOpacity=1.0] - Center opacity (for custom styles)
 * @param {number} [options.edgeOpacity=0.0] - Edge opacity (for custom styles)
 */
export class CustomPointHelper extends Group {
    public sprites: THREE.Sprite[];

    constructor(points: Point[] = [], options: PointHelperOptions = {}) {
        super();
        const {
            size = 0.04,
            texture,
            opacity = 1.0,
            pointStyle = PointStyle.CIRCLE,
            ringWidth = 0.18,
            ringColor = '#fff',
            centerOpacity = 1.00,
            edgeOpacity = 0.0,
        } = options;
        if (!Object.values(PointStyle).includes(pointStyle)) {
            throw new Error(`Invalid pointStyle: ${pointStyle}`);
        }
        // If no texture provided, generate one based on style
        let sharedTexture = null;
        if (!texture) {
            if (pointStyle === PointStyle.RING) {
                sharedTexture = createPointTexture({ size: 256, pointStyle: PointStyle.RING, pointColor: ringColor, ringWidth });
            } else if (pointStyle === PointStyle.CLOUD) {
                sharedTexture = createPointTexture({ size: 256, pointStyle: PointStyle.CLOUD });
            } else {
                sharedTexture = createPointTexture({ size: 256, pointStyle: PointStyle.CIRCLE, pointColor: '#fff' });
            }
        }
        this.sprites = [];
        // Create a sprite for each point
        points.forEach((pt) => {
            const color = new THREE.Color(pt.color || '#ffffff');
            const spriteTexture = texture || sharedTexture;
            const material = new THREE.SpriteMaterial({
                map: spriteTexture,
                color: color,
                opacity: opacity,
                transparent: true,
                alphaTest: 0.0,
                depthTest: true,
                depthWrite: false, // Prevents transparent sprite from occluding text behind
            });
            const sprite = new THREE.Sprite(material);
            sprite.position.set(pt.position[0], pt.position[1], pt.position[2]);
            sprite.scale.set(size, size, 1);
            this.add(sprite);
            this.sprites.push(sprite);
        });
    }
    // Optionally, add update methods for dynamic data
}

export default CustomPointHelper;
